import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '../constants/collections';
import { sanitizeProfile } from '../utils/cogniprint-scoring';
import { mergeProfileForSave } from '../utils/profile-merge';
import { validateProfile, isTestProfile } from '../utils/profile-validation';
import { backfillProvenance } from '../utils/profile-provenance';
import type { CogniPrintProfile } from '../types/cogniprint';

// Normalize a profile from Firestore — handles both snake_case (legacy/AI output)
// and camelCase (our TypeScript types) formats
function normalizeProfile(raw: Record<string, unknown>, userId: string): CogniPrintProfile {
  // If it already has camelCase contentFormat with a score object, it's already normalized
  const cf = raw.contentFormat as Record<string, unknown> | undefined;
  if (cf && typeof cf === 'object' && cf.score && typeof cf.score === 'object') {
    return backfillProvenance(raw as unknown as CogniPrintProfile);
  }

  // Otherwise, it's the AI's snake_case format stored directly in Firestore
  const dims = (raw.dimensions ?? raw) as Record<string, unknown>;
  const cfp = (dims.content_format_preference ?? dims.contentFormat) as Record<string, unknown> | undefined;
  const la = (dims.learning_approach ?? dims.learningApproach) as Record<string, unknown> | undefined;
  const dt = (dims.depth_tolerance ?? dims.depthTolerance) as Record<string, unknown> | undefined;
  const sp = (dims.sequencing_preference ?? dims.sequencing) as Record<string, unknown> | undefined;
  const idp = (dims.information_density_preference ?? dims.infoDensity) as Record<string, unknown> | undefined;
  const md = (dims.motivational_driver ?? dims.motivation) as Record<string, unknown> | undefined;
  const vr = (dims.viewpoint_range ?? dims.viewpointRange) as Record<string, unknown> | undefined;

  const now = new Date().toISOString();

  return backfillProvenance({
    profileVersion: (raw.profile_version ?? raw.profileVersion ?? 1) as number,
    diagnosticType: (raw.diagnostic_type ?? raw.diagnosticType ?? 'quick') as 'quick' | 'full',
    userId,
    createdAt: (raw.createdAt ?? now) as string,
    lastUpdated: (raw.lastUpdated ?? now) as string,
    contentFormat: {
      score: {
        visual: (cfp?.visual as number) ?? 20,
        textual: (cfp?.textual as number) ?? 20,
        auditory: (cfp?.auditory as number) ?? 20,
        interactive: (cfp?.interactive as number) ?? 20,
        video: (cfp?.video as number) ?? 20,
      },
      confidence: (cfp?.confidence as number) ?? 0.5,
    },
    learningApproach: {
      score: {
        theoryApplication: (la?.theory_application_axis ?? la?.theoryApplication ?? 50) as number,
        reflectiveActive: (la?.reflective_active_axis ?? la?.reflectiveActive ?? 50) as number,
      },
      confidence: (la?.confidence as number) ?? 0.5,
    },
    depthTolerance: {
      score: (dt?.score as number) ?? 50,
      confidence: (dt?.confidence as number) ?? 0.5,
    },
    sequencing: {
      score: {
        topDown: (sp?.top_down ?? sp?.topDown ?? 34) as number,
        bottomUp: (sp?.bottom_up ?? sp?.bottomUp ?? 33) as number,
        modular: (sp?.modular ?? 33) as number,
      },
      confidence: (sp?.confidence as number) ?? 0.5,
    },
    infoDensity: idp
      ? { score: (idp.score as number) ?? 50, confidence: (idp.confidence as number) ?? 0.5 }
      : null,
    motivation: md
      ? {
          score: {
            mastery: (md.mastery as number) ?? 25,
            performance: (md.performance as number) ?? 25,
            social: (md.social as number) ?? 25,
            curiosity: (md.curiosity as number) ?? 25,
          },
          confidence: (md.confidence as number) ?? 0.5,
        }
      : null,
    viewpointRange: vr
      ? { score: (vr.score as number) ?? 50, confidence: (vr.confidence as number) ?? 0.5 }
      : null,
    questionsAsked: (raw.questions_asked ?? raw.questionsAsked ?? 0) as number,
    questionsCarriedForward: (raw.questions_carried_forward ?? raw.questionsCarriedForward ?? 0) as number,
    overallConfidence: (raw.overall_confidence ?? raw.overallConfidence ?? 0.5) as number,
    archetypeMatch: (raw.archetype_match ?? raw.archetypeMatch ?? null) as string | null,
    adaptiveNotes: (raw.adaptive_notes ?? raw.adaptiveNotes ?? '') as string,
    summary: (raw.summary as CogniPrintProfile['summary']) ?? null,
    dimensionProvenance: raw.dimensionProvenance as CogniPrintProfile['dimensionProvenance'],
    anchorSetVersion: (raw.anchorSetVersion as string | null) ?? null,
    itemResponses: raw.itemResponses as CogniPrintProfile['itemResponses'],
    evidenceStatusVersion: raw.evidenceStatusVersion as string | undefined,
    isTestProfile: raw.isTestProfile === true,
  });
}

/** Thrown when a profile fails the validation gate. Carries the reason codes. */
export class InvalidProfileError extends Error {
  readonly reasons: string[];
  constructor(reasons: string[]) {
    super(`Profile failed validation: ${reasons.join(', ')}`);
    this.name = 'InvalidProfileError';
    this.reasons = reasons;
  }
}

export async function saveProfile(userId: string, profile: CogniPrintProfile): Promise<void> {
  const ref = doc(db, COLLECTIONS.COGNIPRINTS, userId);

  // Non-destructive merge: an unassessed incoming dimension must not clear an
  // assessed stored one. A paid learner re-taking the quick diagnostic used to
  // lose all three paid dimensions this way. See utils/profile-merge.ts.
  const existingSnap = await getDoc(ref);
  const existing = existingSnap.exists()
    ? normalizeProfile(existingSnap.data() as Record<string, unknown>, userId)
    : null;
  // A stranded Content Lab test profile is not a learner's profile and must not
  // be merged into a real one.
  const mergeBase = existing && !isTestProfile(existing) ? existing : null;
  const merged = mergeProfileForSave(profile, mergeBase);

  // Structural rails: clamp scores/confidence to valid ranges and normalize
  // weighted dimensions to sum to 100 before persisting.
  const sanitized = sanitizeProfile(merged);

  // The gate. A profile that does not pass is not written — not here and not to
  // diagnosticSessions. This is what would have stopped the 2026-06-17 defect.
  const validation = validateProfile(sanitized, sanitized.diagnosticType);
  if (!validation.ok) throw new InvalidProfileError(validation.reasons);

  await setDoc(ref, {
    ...sanitized,
    lastUpdated: new Date().toISOString(),
    _firestoreUpdatedAt: serverTimestamp(),
  });

  // Also mark onboarding completed on the user doc
  const userRef = doc(db, COLLECTIONS.USERS, userId);
  await updateDoc(userRef, { onboardingCompleted: true });
}

export async function loadProfile(userId: string): Promise<CogniPrintProfile | null> {
  const ref = doc(db, COLLECTIONS.COGNIPRINTS, userId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  const profile = normalizeProfile(snapshot.data() as Record<string, unknown>, userId);
  // A synthetic Admin Content Lab profile is test data. Reporting "no profile" is
  // honest; rendering invented dimensions as this learner's own is not. Every
  // surface that reads a profile goes through here, so one guard covers them all.
  if (isTestProfile(profile)) {
    console.warn('[profile] ignoring a Content Lab test profile for', userId);
    return null;
  }
  return profile;
}

export async function hasCompletedDiagnostic(userId: string): Promise<{
  hasQuick: boolean;
  hasFull: boolean;
  profile: CogniPrintProfile | null;
}> {
  const profile = await loadProfile(userId);
  if (!profile) {
    return { hasQuick: false, hasFull: false, profile: null };
  }
  return {
    hasQuick: true,
    hasFull: profile.diagnosticType === 'full',
    profile,
  };
}
